/**
 * «کِی؟»ِ یه سیگنال، وقتی تاریخِ مشخص داره (مایگریشن 0057).
 *
 * سه چیزِ مستقل که با هم چهار شکلِ ممکن می‌سازن:
 *   یه روز · بی‌ساعت    ← حالتِ معمول
 *   یه روز · با ساعت
 *   بازه‌ی چند روزه · بی‌ساعت
 *   بازه‌ی چند روزه · با ساعت
 *
 * منطقِ خالص و بی‌وابستگی به React، تا هم تقویم ازش استفاده کنه هم فرمِ ساخت،
 * و هم بشه تستش کرد (نگاه کن به dateChoice.test.ts). قانونِ «لمسِ دوم بازه
 * می‌سازه» جاییه که راحت خراب می‌شه، پس همون‌جا قفل شده.
 */

/** ساعت و دقیقه‌ی شروع. سیگنال ساعتِ پایان نداره - نگاه کن به مایگریشن 0057. */
export interface Clock { h: number; m: number }

export interface DateChoice {
  /** روزِ شروع، نیمه‌شبِ محلی. `null` یعنی هنوز هیچ روزی انتخاب نشده. */
  start: Date | null;
  /** روزِ پایانِ بازه، نیمه‌شبِ محلی. `null` یعنی یه روزه. */
  end: Date | null;
  /** ساعتِ شروع، فقط اگه کاربر **خواسته** باشه. `null` یعنی «فقط روز». */
  time: Clock | null;
}

export const EMPTY_CHOICE: DateChoice = { start: null, end: null, time: null };

/** نیمه‌شبِ همون روز، به وقتِ محلی. */
export const dayOf = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const sameDay = (a: Date, b: Date) => dayOf(a).getTime() === dayOf(b).getTime();

/**
 * لمسِ یه روز روی تقویم.
 *
 * چهار قانون، به همین ترتیب:
 *   ۱. هیچی انتخاب نشده          → همون روز، تنها.
 *   ۲. بازه‌ی کامل داریم         → از نو شروع کن؛ همون روز، تنها.
 *   ۳. روزِ دوم **بعد** از اولیه  → بازه.
 *   ۴. روزِ دوم قبل (یا خودِ اولی) → دوباره تک‌روز، روی همین روزِ تازه.
 *
 * قانونِ چهارم خواسته‌ی صریحِ کاربره: کسی که تاریخِ اشتباه زده و بعد یه روزِ
 * عقب‌تر می‌زنه، داره **تصحیح** می‌کنه، نه بازه‌ی برعکس می‌سازه.
 *
 * جنریکه چون تاریخِ رخداد هم همین دو فیلد رو داره ولی ساعتِ شروع و پایانِ
 * جدا (نگاه کن به core/lib/eventDates.ts) — و قانونِ لمس باید توی هر دو
 * دقیقاً یکی باشه، وگرنه تقویمِ دو صفحه دو جور رفتار می‌کنه.
 */
export function rangeTap<T extends { start: Date | null; end: Date | null }>(c: T, d: Date): T {
  const day = dayOf(d);
  if (!c.start || c.end || day.getTime() <= dayOf(c.start).getTime()) {
    return { ...c, start: day, end: null };
  }
  return { ...c, start: dayOf(c.start), end: day };
}

/** همون `rangeTap`، برای انتخابِ تاریخِ سیگنال. */
export const applyDayTap = (c: DateChoice, d: Date): DateChoice => rangeTap(c, d);

/**
 * لمسِ «فقط یک روز»: هر لمس جای قبلی می‌نشیند، بازه‌ای در کار نیست.
 *
 * برای «تأیید نهایی»: برنامه‌ی قطعی یک لحظه‌ی شروع دارد، نه بازه — یادآور و
 * فایلِ تقویم هر دو به همان یک لحظه بسته‌اند. با `rangeTap` لمسِ دومِ
 * جلوتر بازه می‌ساخت و می‌شد یک «قطعیِ» چندروزه ثبت کرد که هیچ‌کدام از آن
 * دو معنایی برایش نداشتند.
 *
 * `end` همیشه null برمی‌گردد، حتی اگر ورودی داشته باشد — سیگنالِ چندروزه که
 * واردِ پنلِ تأیید می‌شود همین‌جا تک‌روزه می‌شود.
 */
export const applySingleDayTap = (c: DateChoice, d: Date): DateChoice =>
  ({ ...c, start: dayOf(d), end: null });

/** ساعتِ پیش‌فرضِ لحظه‌ای که کاربر می‌گه «ساعت هم می‌خوام». */
export const DEFAULT_CLOCK: Clock = { h: 19, m: 0 };

/**
 * لحظه‌ای که به‌عنوانِ `starts_at` ذخیره می‌شه.
 *
 * بی‌ساعت که باشه **ظهر** می‌شینه، نه نیمه‌شب. چون ستون timestamptz است و
 * نیمه‌شبِ محلی با یه جابه‌جاییِ منطقه‌ی زمانی می‌افته توی روزِ قبل یا بعد —
 * ظهر دوازده ساعت حاشیه‌ی امن از هر دو طرف داره. ساعتش هیچ‌جا نشون داده
 * نمی‌شه؛ `date_only` توی دیتابیس همینو می‌گه.
 */
export function startInstant(c: DateChoice): Date | null {
  if (!c.start) return null;
  const d = dayOf(c.start);
  d.setHours(c.time?.h ?? 12, c.time?.m ?? 0, 0, 0);
  return d;
}

/** لحظه‌ی `ends_at`. همیشه ظهرِ روزِ پایان: بازه ساعتِ پایان نداره. */
export function endInstant(c: DateChoice): Date | null {
  if (!c.end) return null;
  const d = dayOf(c.end);
  d.setHours(12, 0, 0, 0);
  return d;
}

/** چیزی انتخاب شده؟ */
export const hasDate = (c: DateChoice): boolean => c.start !== null;

/** همون انتخاب، از روی چیزی که قبلاً ذخیره شده. */
export function choiceFrom(
  s: { startsAt?: string | null; endsAt?: string | null; dateOnly?: boolean | null },
): DateChoice {
  if (!s.startsAt) return EMPTY_CHOICE;
  const start = new Date(s.startsAt);
  const end = s.endsAt ? new Date(s.endsAt) : null;
  return {
    start: dayOf(start),
    end: end && !sameDay(start, end) ? dayOf(end) : null,
    time: s.dateOnly ? null : { h: start.getHours(), m: start.getMinutes() },
  };
}
